/**
 * Browser Web Locks for upload jobs (DE-1711, ADR-0006).
 *
 * The tab that owns an upload job holds the exclusive lock "dams-upload-job:<jobId>" until the job is
 * finished (review completed or cancelled) or the tab closes, when the browser releases it. Another tab
 * resurrecting incomplete jobs only claims, or reattaches to (DE-1730), a job whose lock it can take at once,
 * so it never takes over a job that is still uploading or waiting for review in another tab of the same browser.
 *
 * Without Web Locks every call succeeds, and the server-side claim alone prevents duplicates.
 */

const lockName = (jobId) => `dams-upload-job:${jobId}`;

/**
 * Creates the lock functions for a LockManager, or for none when Web Locks are unavailable.
 *
 * @param {LockManager|undefined} locks - Usually navigator.locks.
 * @returns {{acquireJobLock: function(string): Promise<function|null>,
 *            holdJobLockIfAvailable: function(string): Promise<boolean>,
 *            tryJobLock: function(string, function): Promise<{acquired: boolean, result?: *}>,
 *            releaseJobLock: function(string): void}}
 */
export const createUploadJobLocks = (locks) => {
    const held = new Map();

    /**
     * Takes the job's lock and holds it until releaseJobLock(jobId). Waits if another tab holds it.
     *
     * @returns {Promise<function|null>} The release function, or null without Web Locks.
     */
    const acquireJobLock = async (jobId) => {
        if (!locks) {
            return null;
        }
        if (held.has(jobId)) {
            return held.get(jobId);
        }
        return hold(jobId, {mode: 'exclusive'});
    };

    /**
     * Takes the job's lock only if it is free right now, and then holds it until releaseJobLock(jobId), like
     * acquireJobLock. Never waits (DE-1730: reattaching to a job waiting for review).
     *
     * @returns {Promise<boolean>} true if this tab holds the lock now (or there are no Web Locks), false if
     * another tab holds it.
     */
    const holdJobLockIfAvailable = async (jobId) => {
        if (!locks || held.has(jobId)) {
            return true;
        }
        return await hold(jobId, {mode: 'exclusive', ifAvailable: true}) !== null;
    };

    /**
     * Runs action while holding the job's lock, only if the lock is free right now. Never waits.
     *
     * @returns {Promise<{acquired: boolean, result?: *}>}
     */
    const tryJobLock = async (jobId, action) => {
        if (!locks) {
            return {acquired: true, result: await action()};
        }
        return locks.request(lockName(jobId), {ifAvailable: true}, async (lock) =>
            lock ? {acquired: true, result: await action()} : {acquired: false});
    };

    /** Releases the job's lock if this tab holds it. */
    const releaseJobLock = (jobId) => {
        held.get(jobId)?.();
    };

    /**
     * Requests the job's lock with the given options and, once granted, holds it until released.
     *
     * @returns {Promise<function|null>} The release function, or null if the lock was not granted
     * (only possible with ifAvailable).
     */
    const hold = async (jobId, options) => {
        let release;
        const releasedWhenDone = new Promise(resolve => {
            release = resolve;
        });
        const granted = await new Promise(resolveGranted => {
            locks.request(lockName(jobId), options, (lock) => {
                resolveGranted(lock !== null);
                return lock ? releasedWhenDone : undefined;
            });
        });
        if (!granted) {
            return null;
        }
        const releaseJob = () => {
            held.delete(jobId);
            release();
        };
        held.set(jobId, releaseJob);
        return releaseJob;
    };

    return {acquireJobLock, holdJobLockIfAvailable, tryJobLock, releaseJobLock};
};

export const {
    acquireJobLock,
    holdJobLockIfAvailable,
    tryJobLock,
    releaseJobLock
} = createUploadJobLocks(globalThis.navigator?.locks);
